import { Spacer } from "../atoms/Atoms"
import React from "react"
import { useSelector } from "react-redux"
import styled from "styled-components"
import L10n from "../../../lib/carrot-l10n"
import { getInterest, getJourney, getTalkedToDoctor } from "../../../reducers/userInfo"
import { getInterestIsFertility } from "derivedSelectors"
import Journeys from "utils/Journeys"
import Interests from "utils/Interests"
import { IlloLibrary } from "../atoms/Illustrations"
import { RoundedRectangle } from "../atoms/roundedrectangle/RoundedRectangle"
import { BodySmall } from "../atoms/Bodies"
import { ArrowLink } from "../atoms/links/ArrowLink"
import { colors, H2 } from "@carrotfertility/carotene"

const l = new L10n({ locale: "en-US" }).getBoundLocalize()

const HeroHeader = (props) => <H2 {...props} variant="base" />
const StyledHeroHeader = styled(HeroHeader)({
  color: colors.base.white
})

const HeroText = styled.span`
  font-size: 17px;
  line-height: 1.5em;
`

const ReadAndLearnHeroTemplate = ({ title, bodyCopy, ctas }) => (
  <div className="flex flex-auto flex-row">
    <RoundedRectangle padding="pa0" backgroundColor="dark-blue">
      <div className="flex flex-row-reverse flex-wrap justify-end">
        <div className="flex pa35">
          <IlloLibrary />
        </div>
        <div className="w-50 flex flex-column flex-grow-1 min-w250px pa35">
          <StyledHeroHeader>{title}</StyledHeroHeader>
          <Spacer height={0.25} />
          <BodySmall font="publico-text" color="white">
            <HeroText>{bodyCopy}</HeroText>
          </BodySmall>
          <Spacer height={1.5} />
          {ctas &&
            ctas.map((cta) => (
              <React.Fragment key={cta.uri}>
                <ArrowLink to={cta.uri} color="white">
                  {cta.name}
                </ArrowLink>
                <Spacer height={1} />
              </React.Fragment>
            ))}
          {ctas && ctas.length > 0 ? (
            <ArrowLink to="/library" color="white">
              {l`client.home.browseAllTopics`}
            </ArrowLink>
          ) : (
            <ArrowLink to="/library" color="white">
              {l`client.home.startBrowsing`}
            </ArrowLink>
          )}
        </div>
      </div>
    </RoundedRectangle>
  </div>
)

const getBasicReadAndLearnHeroModule = () => (
  <ReadAndLearnHeroTemplate
    title="Read + learn: Fertility basics"
    bodyCopy="Brush up on the basics and read answers to common questions."
    ctas={[
      {
        uri: "/read/Fertility-basics",
        name: "Read about fertility basics"
      }
    ]}
  />
)

const getFertilityModules = ({ interest, journey, talkedToDoctor }) => {
  if (talkedToDoctor || journey === Journeys.TAKING_ACTION) {
    const interestIsFertilityBasics = [Interests.UNDERSTAND_MY_FERTILITY, Interests.INITIAL_FERTILITY_TESTING].includes(
      interest
    )

    const interestIsIuiOrSomethingElse = [Interests.IUI, "SOMETHING_ELSE"].includes(interest)

    if (interestIsFertilityBasics) {
      return getBasicReadAndLearnHeroModule()
    }

    if (interestIsIuiOrSomethingElse) {
      return (
        <ReadAndLearnHeroTemplate
          title="Read + learn"
          bodyCopy="A wealth of knowledge right at your fingertips, featuring expert-approved articles and instructional videos."
        />
      )
    }

    if (interest === Interests.EGG_FREEZING) {
      return (
        <ReadAndLearnHeroTemplate
          title="Read + learn: Egg freezing"
          bodyCopy="Understand the different stages of egg freezing and what to expect at each step."
          ctas={[
            {
              uri: "/read/Preservation#Freezing-eggs",
              name: "Read about egg freezing"
            }
          ]}
        />
      )
    }

    if (interest === Interests.SPERM_FREEZING) {
      return (
        <ReadAndLearnHeroTemplate
          title="Read + learn: Preservation"
          bodyCopy="Get answers to common questions about freezing sperm, plus questions to ask your clinic."
          ctas={[{ uri: "/read/Preservation", name: "Read about preservation" }]}
        />
      )
    }

    if (interest === Interests.EMBRYO_FREEZING) {
      return (
        <ReadAndLearnHeroTemplate
          title="Read + learn: Embryo freezing"
          bodyCopy="From medications to vitrification, learn about the different stages of embryo freezing."
          ctas={[
            {
              uri: "/read/Preservation#Freezing-embryos",
              name: "Read about embryo freezing"
            }
          ]}
        />
      )
    }

    if (interest === Interests.IVF) {
      return (
        <ReadAndLearnHeroTemplate
          title="Read + learn: In vitro fertilization"
          bodyCopy="Also known as IVF, learn about the different steps and medications in this multi-phase process."
          ctas={[{ uri: "/read/IVF", name: "Read about IVF" }]}
        />
      )
    }
  } else {
    //groups 1A + 2A
    return (
      <ReadAndLearnHeroTemplate
        title="Read + learn: Initial fertility consultation"
        bodyCopy="When it comes to understanding your fertility, an initial consultation is a great place to start."
        ctas={[
          {
            uri: "/read/Initial-consultation",
            name: "Read about initial consultations"
          }
        ]}
      />
    )
  }
}

const ReadAndLearnHero = () => {
  const { interest, journey, interestIsFertility, talkedToDoctor } = useSelector((state) => ({
    interest: getInterest(state),
    journey: getJourney(state),
    interestIsFertility: getInterestIsFertility(state),
    talkedToDoctor: getTalkedToDoctor(state)
  }))

  if (journey === Journeys.PREGNANCY || interest === Interests.PREGNANCY) {
    return (
      <ReadAndLearnHeroTemplate
        title="Read + learn: Pregnancy"
        bodyCopy="Brush up on the basics and read answers to common questions."
        ctas={[
          {
            uri: "/read/Pregnancy",
            name: "Read about pregnancy"
          }
        ]}
      />
    )
  }

  if (interest === Interests.TRYING_TO_GET_PREGNANT) {
    return getBasicReadAndLearnHeroModule()
  }

  if (interestIsFertility) {
    return getFertilityModules({ interest, journey, talkedToDoctor })
  }

  if (interest === Interests.ADOPTING) {
    return (
      <ReadAndLearnHeroTemplate
        title="Read + learn: Adoption"
        bodyCopy="Get the basics on different types of adoption, and what you can expect in terms of cost and timing."
        ctas={[{ uri: "/read/Adoption", name: "Read about adoption" }]}
      />
    )
  }

  if (interest === Interests.GESTATIONAL_CARRIER) {
    return (
      <ReadAndLearnHeroTemplate
        title="Read + learn: Gestational carrier journeys"
        bodyCopy="Learn more about finding donors and the gestational carrier (commonly called surrogacy) process."
        ctas={[
          {
            uri: "/read/Third-party-reproduction#Donated-eggs-sperm-and-embryos",
            name: "Read about donor eggs, sperm, and embryos"
          },
          {
            uri: "/read/Third-party-reproduction#Gestational-carrier-arrangements-in-the-U.S.",
            name: "Read about gestational carriers"
          }
        ]}
      />
    )
  }

  return <ReadAndLearnHeroTemplate title={l`client.library.title`} bodyCopy={l`client.home.aWealthOfKnowledge`} />
}

export default ReadAndLearnHero
